/* eslint-disable react-refresh/only-export-components */
import type { PresentationData } from '../types'
import {
  EnhancedContentSlide,
  EnhancedGridSlide,
  FeatureCard,
} from '../components/slides'
import { IconSolid } from '../components/ui/Icon'
import { Card } from '../components/ui/Card'

/**
 * AI Consulting Initial Meeting Presentation
 * 
 * Initial consultation presentation for AI consulting and RAG systems.
 * Custom styling for business/consulting presentations with geometric backgrounds.
 */

// ============================================
// CUSTOM COMPONENTS FOR THIS PRESENTATION
// ============================================

interface GeometricTitleSlideProps {
  title: string
  subtitle?: string
  author1?: { name: string; role: string }
  author2?: { name: string; role: string }
  valueProp1?: { icon: string; text: string }
  valueProp2?: { icon: string; text: string }
  valueProp3?: { icon: string; text: string }
  sessionDuration?: string
  sessionConfidentiality?: string
  sessionAgenda?: string
}

function GeometricTitleSlide({
  subtitle,
  author1,
  author2,
  valueProp1,
  valueProp2,
  valueProp3,
  sessionDuration,
  sessionConfidentiality,
  sessionAgenda,
}: GeometricTitleSlideProps) {
  return (
  <div className="relative w-full h-screen overflow-hidden bg-gradient-to-br from-slate-900 via-blue-950 to-blue-900">
    {/* Geometric Background */}
    <div className="absolute inset-0 overflow-hidden">
      {/* Floating Circles */}
      <div className="absolute -top-32 -right-24 w-72 h-72 bg-blue-400/10 rounded-full blur-sm animate-float" />
      <div className="absolute -bottom-24 -left-12 w-48 h-48 bg-emerald-400/8 rounded-full blur-sm animate-float-reverse" />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-radial from-blue-500/5 via-transparent to-transparent" />
    </div>

    {/* Content Grid */}
    <div className="relative z-10 grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-8 lg:gap-16 h-full px-8 lg:px-20 py-16 pb-24 items-center">
      {/* Left Column */}
      <div className="space-y-6 lg:space-y-8">
        {/* Company Badge */}
        <div className="inline-flex items-center gap-3 bg-blue-500/20 border border-blue-400/30 px-6 py-3 rounded-full text-blue-300 font-medium backdrop-blur-sm">
          <IconSolid name="robot" className="w-4 h-4" />
          <span>AI Expertise</span>
        </div>

        {/* Main Title */}
        <h1 className="text-5xl lg:text-7xl font-light text-white leading-tight">
          <span className="font-bold bg-gradient-to-r from-blue-400 to-emerald-400 bg-clip-text text-transparent">
            AI
          </span>
          <span className="text-white/60 font-extralight"> </span>
          <span className="font-normal">Consulting</span>
          <br />
          <span className="font-extralight italic text-white/80 text-4xl lg:text-6xl">&</span>{' '}
          <span className="font-normal">Development</span>
        </h1>

        {/* Subtitle */}
        {subtitle && (
          <h2 className="text-2xl lg:text-3xl font-light text-white/90 leading-relaxed">
            <span className="text-blue-400 font-medium">Initial Meeting:</span>
            <br />
            {subtitle}
          </h2>
        )}

        {/* Value Props */}
        <div className="flex flex-wrap gap-4 lg:gap-8">
          {valueProp1 && (
            <div className="flex items-center gap-3 bg-white/10 px-6 py-4 rounded-xl text-white font-medium backdrop-blur-sm border border-white/20">
              <IconSolid name={valueProp1.icon} className="w-5 h-5 text-blue-400" />
              <span>{valueProp1.text}</span>
            </div>
          )}
          {valueProp2 && (
            <div className="flex items-center gap-3 bg-white/10 px-6 py-4 rounded-xl text-white font-medium backdrop-blur-sm border border-white/20">
              <IconSolid name={valueProp2.icon} className="w-5 h-5 text-blue-400" />
              <span>{valueProp2.text}</span>
            </div>
          )}
          {valueProp3 && (
            <div className="flex items-center gap-3 bg-white/10 px-6 py-4 rounded-xl text-white font-medium backdrop-blur-sm border border-white/20">
              <IconSolid name={valueProp3.icon} className="w-5 h-5 text-blue-400" />
              <span>{valueProp3.text}</span>
            </div>
          )}
        </div>
      </div>

      {/* Right Column */}
      <div className="space-y-6 lg:space-y-8">
        {/* Presenter Card */}
        {(author1 || author2) && (
          <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
            <div className="p-6 lg:p-8">
              <h3 className="text-xl lg:text-2xl font-semibold text-blue-300 mb-6">Your Experts</h3>
              <div className="space-y-6">
                {author1 && (
                  <div className="flex items-center gap-4">
                    <img 
                      src="/images/jan.png" 
                      alt={author1.name}
                      className="w-20 h-20 rounded-2xl object-cover flex-shrink-0"
                    />
                    <div>
                      <h4 className="text-lg font-semibold">{author1.name}</h4>
                      <p className="text-white/90 text-sm font-medium">{author1.role}</p>
                    </div>
                  </div>
                )}
                {author2 && (
                  <div className="flex items-center gap-4">
                    <img 
                      src="/images/kai.png" 
                      alt={author2.name}
                      className="w-20 h-20 rounded-2xl object-cover flex-shrink-0"
                    />
                    <div>
                      <h4 className="text-lg font-semibold">{author2.name}</h4>
                      <p className="text-white/90 text-sm font-medium">{author2.role}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Session Details */}
        {(sessionDuration || sessionConfidentiality || sessionAgenda) && (
          <Card className="bg-black/20 backdrop-blur-md border-white/10 text-white">
            <div className="p-6 lg:p-8 space-y-4">
              {sessionDuration && (
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-blue-400/20 rounded-lg flex items-center justify-center">
                    <IconSolid name="clock" className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <div className="text-xs text-white/70 font-medium uppercase tracking-wide">Duration</div>
                    <div className="font-medium text-white">{sessionDuration}</div>
                  </div>
                </div>
              )}
              {sessionConfidentiality && (
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-blue-400/20 rounded-lg flex items-center justify-center">
                    <IconSolid name="shield-alt" className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <div className="text-xs text-white/70 font-medium uppercase tracking-wide">Confidentiality</div>
                    <div className="font-medium text-white">{sessionConfidentiality}</div>
                  </div>
                </div>
              )}
              {sessionAgenda && (
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-blue-400/20 rounded-lg flex items-center justify-center">
                    <IconSolid name="calendar-alt" className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <div className="text-xs text-white/70 font-medium uppercase tracking-wide">Agenda</div>
                    <div className="font-medium text-white">{sessionAgenda}</div>
                  </div>
                </div>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>

    <style>{`
      @keyframes float {
        0%, 100% { transform: translateY(0px) rotate(0deg); }
        50% { transform: translateY(-20px) rotate(5deg); }
      }
      @keyframes float-reverse {
        0%, 100% { transform: translateY(0px) rotate(0deg); }
        50% { transform: translateY(-20px) rotate(-5deg); }
      }
      .animate-float {
        animation: float 6s ease-in-out infinite;
      }
      .animate-float-reverse {
        animation: float-reverse 8s ease-in-out infinite;
      }
      .bg-gradient-radial {
        background: radial-gradient(ellipse at top right, rgba(59, 130, 246, 0.1) 0%, transparent 50%);
      }
    `}</style>
  </div>
  )
}

// Numbered Benefit Card with custom styling
interface NumberedBenefitCardProps {
  number: string
  title: string
  description: string
}

function NumberedBenefitCard({ number, title, description }: NumberedBenefitCardProps) {
  return (
  <Card className="bg-slate-800/95 border-slate-600 hover:-translate-y-1 transition-transform duration-300 shadow-lg">
    <div className="p-6">
      <div className="text-5xl font-bold text-blue-400 mb-4">{number}</div>
      <h4 className="text-xl font-semibold text-white mb-2">{title}</h4>
      <p className="text-white/90 font-medium">{description}</p>
    </div>
  </Card>
  )
}

// Architecture Layer Component
interface ArchLayerProps {
  icon: string
  title: string
  description: string
  gradient: string
}

function ArchLayer({ icon, title, description, gradient }: ArchLayerProps) {
  return (
  <div className={`p-4 lg:p-5 rounded-2xl text-white shadow-lg hover:translate-x-2 transition-transform ${gradient}`}>
    <h4 className="text-lg lg:text-xl font-semibold mb-1 flex items-center gap-2">
      <IconSolid name={icon} className="w-5 h-5" />
      {title}
    </h4>
    <p className="text-white/90 text-sm lg:text-base">{description}</p>
  </div>
  )
}

// ============================================
// PRESENTATION DATA
// ============================================

export const erstgespraechEn: PresentationData = {
  metadata: {
    title: 'AI Consulting Initial Meeting',
    subtitle: 'Structured Initial Meeting for AI Consulting and RAG Systems',
    author: 'Jan Pfrommer & Kai Bennett',
    date: '2025',
    duration: 90,
    tags: ['AI', 'Consulting', 'RAG', 'Business'],
    description: 'Initial consultation presentation for AI advisory services, covering client needs assessment, AI maturity, and solution options.',
  },
  slides: [
    // Slide 1: Custom Title Slide
    {
      id: 1,
      component: () => (
        <GeometricTitleSlide
          title="AI Consulting & Development"
          subtitle="Your Path to Successful AI Integration"
          author1={{ name: 'Jan Pfrommer', role: 'Senior Software Engineer & AI Engineer' }}
          author2={{ name: 'Kai Bennett', role: 'Software Architect & AI Cloud Engineer' }}
          valueProp1={{ icon: 'lightbulb', text: 'Strategic Consulting' }}
          valueProp2={{ icon: 'cogs', text: 'RAG Systems' }}
          valueProp3={{ icon: 'graduation-cap', text: 'Practical Training' }}
          sessionDuration="60-90 Minutes"
          sessionConfidentiality="Fully Guaranteed"
          sessionAgenda="Structured & Efficient"
        />
      ),
      transition: 'fade',
    },

    // Slide 2: Meeting Objectives
    {
      id: 2,
      component: () => (
        <EnhancedContentSlide
          title="💡 Meeting Objectives"
          subtitle="What we want to achieve together today"
          variant="light"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            <NumberedBenefitCard
              number="01"
              title="Understand Your Situation"
              description="Learn about your current challenges and pain points"
            />
            <NumberedBenefitCard
              number="02"
              title="Assess AI Maturity"
              description="Evaluate technical and organizational prerequisites"
            />
            <NumberedBenefitCard
              number="03"
              title="Find Suitable Solutions"
              description="Consulting, training, or development - what makes sense for you?"
            />
            <NumberedBenefitCard
              number="04"
              title="Demonstrate Feasibility"
              description="Live demo of our RAG system - not theory, but working technology"
            />
          </div>
        </EnhancedContentSlide>
      ),
      transition: 'slide',
    },

    // Slide 3: Agenda
    {
      id: 3,
      component: () => (
        <EnhancedContentSlide
          title="Meeting Flow"
          subtitle="60-90 minutes structured and efficient"
          variant="light"
        >
          <div className="flex flex-col gap-2 mt-4 max-w-4xl mx-auto pb-24">
            <ArchLayer
              icon="handshake"
              title="1. Introduction (5 min)"
              description="Introductions • Clarify goals • Framework"
              gradient="bg-gradient-to-r from-blue-900 to-slate-900"
            />
            <div className="text-center text-2xl text-blue-600 font-bold">↓</div>
            <ArchLayer
              icon="search"
              title="2. Client Context (15-20 min)"
              description="Processes • AI experience • Data sources • Requirements"
              gradient="bg-gradient-to-r from-teal-700 to-teal-900"
            />
            <div className="text-center text-2xl text-blue-600 font-bold">↓</div>
            <ArchLayer
              icon="rocket"
              title="3. Demo & Insights (15-25 min)"
              description="RAG system live • Best practices • Highlight potential"
              gradient="bg-gradient-to-r from-amber-600 to-amber-800"
            />
            <div className="text-center text-2xl text-blue-600 font-bold">↓</div>
            <ArchLayer
              icon="puzzle-piece"
              title="4. Service Modules (10-15 min)"
              description="Modular offerings • Find suitable solutions"
              gradient="bg-gradient-to-r from-slate-600 to-slate-800"
            />
            <div className="text-center text-2xl text-blue-600 font-bold">↓</div>
            <ArchLayer
              icon="forward"
              title="5. Next Steps (5-10 min)"
              description="Summary • Pilot project • Concrete steps"
              gradient="bg-gradient-to-r from-emerald-600 to-emerald-800"
            />
          </div>
        </EnhancedContentSlide>
      ),
      transition: 'slide',
    },

    // Slide 4: Kai Bennett (Presenter Slide)
    {
      id: 4,
      component: () => (
        <div className="relative w-full h-screen bg-gradient-to-br from-slate-600 to-slate-900 overflow-hidden">
          <div className="relative z-10 flex items-center justify-center h-full p-6 pb-24">
            <div className="max-w-6xl text-white w-full">
              {/* Header with Image and Name */}
              <div className="flex items-center gap-6 mb-6">
                <img 
                  src="/images/kai.png" 
                  alt="Kai Bennett"
                  className="w-28 h-28 rounded-2xl object-cover shadow-2xl flex-shrink-0"
                />
                <div className="text-left">
                  <h2 className="text-4xl font-bold">Kai Bennett</h2>
                  <p className="text-xl font-medium text-white/90 mt-1">
                    <strong>Software Architect & AI Cloud Engineer</strong>
                  </p>
                  <p className="text-base text-white/80 mt-1">
                    10+ years experience in .NET development • AI & RAG systems since 2023
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Left Column: Education & Certifications */}
                <div className="space-y-4">
                  {/* Education */}
                  <div className="p-4 bg-white/8 rounded-lg border-l-4 border-white/40">
                    <h4 className="text-white mb-2 text-sm font-semibold flex items-center gap-2">
                      <IconSolid name="graduation-cap" className="w-4 h-4" />
                      Education
                    </h4>
                    <div className="space-y-2 text-xs">
                      <div className="text-white/90">
                        <strong>B.Sc. Applied Computer Science</strong><br />
                        <span className="text-white/70">FHDW | 2013–2015</span>
                      </div>
                      <div className="text-white/90">
                        <strong>IT Technical Assistant</strong><br />
                        <span className="text-white/70">Lippe Berufskolleg | 2010–2013</span>
                      </div>
                    </div>
                  </div>

                  {/* Microsoft Certifications */}
                  <div className="p-4 bg-white/10 rounded-xl">
                    <h4 className="text-white mb-3 text-sm font-semibold flex items-center gap-2">
                      <IconSolid name="certificate" className="w-4 h-4" />
                      Certifications
                    </h4>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center">
                        <img 
                          src="https://www.certible.com/_astro/isaqb.Das6p-35_ZRV8sz.webp" 
                          alt="Software Architecture" 
                          className="w-16 h-16 mb-1 mx-auto"
                        />
                        <p className="text-xs text-white/90">
                          <strong>CPSA-F</strong>
                        </p>
                      </div>
                      <div className="text-center">
                        <img 
                          src="https://www.certible.com/_astro/isaqb.Das6p-35_ZRV8sz.webp" 
                          alt="Software Engineering" 
                          className="w-16 h-16 mb-1 mx-auto"
                        />
                        <p className="text-xs text-white/90">
                          <strong>CPSA-A</strong>
                        </p>
                      </div>
                      <div className="text-center">
                        <img 
                          src="https://www.certible.com/_astro/isaqb.Das6p-35_ZRV8sz.webp" 
                          alt="iSAQB CPSA-F" 
                          className="w-16 h-16 mb-1 mx-auto object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                        <div className="w-16 h-16 items-center justify-center bg-white/20 rounded-full mx-auto mb-1" style={{ display: 'none' }}>
                          <IconSolid name="graduation-cap" className="w-8 h-8 text-white" />
                        </div>
                        <p className="text-xs text-white/90">
                          <strong>iSAQB CPSA-F</strong>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column: Skills Grid */}
                <div className="grid grid-cols-1 gap-3">
                  <Card className="bg-white/15 border-white/30 text-white backdrop-blur-sm">
                    <div className="p-4">
                      <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                        <IconSolid name="cloud" className="w-4 h-4" />
                        AI & Infrastructure
                      </h4>
                      <ul className="text-xs space-y-1 text-white/90 text-left">
                        <li>• Open-Source LLMs • Document Intelligence</li>
                        <li>• AI Search • Container Apps</li>
                        <li>• Functions & Service Bus • SQL Databases</li>
                      </ul>
                    </div>
                  </Card>

                  <Card className="bg-white/15 border-white/30 text-white backdrop-blur-sm">
                    <div className="p-4">
                      <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                        <IconSolid name="code" className="w-4 h-4" />
                        Development
                      </h4>
                      <ul className="text-xs space-y-1 text-white/90 text-left">
                        <li>• Python (FastAPI, RAG) • C# & .NET</li>
                        <li>• Qdrant Vector DB • Docker & Kubernetes</li>
                        <li>• REST APIs • Microservices</li>
                      </ul>
                    </div>
                  </Card>

                  <Card className="bg-white/15 border-white/30 text-white backdrop-blur-sm">
                    <div className="p-4">
                      <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                        <IconSolid name="tools" className="w-4 h-4" />
                        Architecture & DevOps
                      </h4>
                      <ul className="text-xs space-y-1 text-white/90 text-left">
                        <li>• Software Architecture • Infrastructure Setup</li>
                        <li>• DevOps • CI/CD Pipelines</li>
                        <li>• Dapr & Service Mesh • IaC</li>
                      </ul>
                    </div>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
      transition: 'fade',
    },

    // Slide 5: Jan Pfrommer (Presenter Slide)
    {
      id: 5,
      component: () => (
        <div className="relative w-full h-screen bg-gradient-to-br from-teal-700 to-teal-900 overflow-hidden">
          <div className="relative z-10 flex items-center justify-center h-full p-6 pb-24">
            <div className="max-w-6xl text-white w-full">
              {/* Header with Image and Name */}
              <div className="flex items-center gap-6 mb-6">
                <img 
                  src="/images/jan.png" 
                  alt="Jan Pfrommer"
                  className="w-28 h-28 rounded-2xl object-cover shadow-2xl flex-shrink-0"
                />
                <div className="text-left">
                  <h2 className="text-4xl font-bold">Jan Pfrommer</h2>
                  <p className="text-xl font-medium text-white/90 mt-1">
                    <strong>Senior Software Engineer & AI Engineer</strong>
                  </p>
                  <p className="text-base text-white/80 mt-1">
                    Frontend, Backend & Requirements Engineering • Scrum Master & AI Projects
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Left Column: Education & Certifications */}
                <div className="space-y-4">
                  {/* Education */}
                  <div className="p-4 bg-white/8 rounded-lg border-l-4 border-white/40">
                    <h4 className="text-white mb-2 text-sm font-semibold flex items-center gap-2">
                      <IconSolid name="graduation-cap" className="w-4 h-4" />
                      Education
                    </h4>
                    <div className="space-y-2 text-xs">
                      <div className="text-white/90">
                        <strong>M.Sc. Information Systems</strong><br />
                        <span className="text-white/70">HS Pforzheim | 2017–2019</span>
                      </div>
                      <div className="text-white/90">
                        <strong>B.Sc. Business IT</strong><br />
                        <span className="text-white/70">HS Pforzheim | 2012–2017</span>
                      </div>
                    </div>
                  </div>

                  {/* Certifications */}
                  <div className="p-4 bg-white/10 rounded-xl">
                    <h4 className="text-white mb-3 text-sm font-semibold flex items-center gap-2">
                      <IconSolid name="certificate" className="w-4 h-4" />
                      Certifications
                    </h4>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center">
                        <img 
                          src="https://www.mitsm.de/wp-content/uploads/2024/11/RZ_IREB_Badges_CPRE_TRAIN_2025-2.svg" 
                          alt="CPRE-FL" 
                          className="w-16 h-16 mb-1 mx-auto object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                        <div className="w-16 h-16 items-center justify-center bg-white/20 rounded-full mx-auto mb-1" style={{ display: 'none' }}>
                          <IconSolid name="clipboard-check" className="w-8 h-8 text-white" />
                        </div>
                        <p className="text-xs text-white/90">
                          <strong>CPRE-FL</strong>
                        </p>
                      </div>
                      <div className="text-center">
                        <img 
                          src="https://logodix.com/logo/649849.jpg" 
                          alt="ISTQB CTFL" 
                          className="w-16 h-16 mb-1 mx-auto object-contain bg-white/10 rounded-lg"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                        <div className="w-16 h-16 items-center justify-center bg-white/20 rounded-full mx-auto mb-1" style={{ display: 'none' }}>
                          <IconSolid name="check-circle" className="w-8 h-8 text-white" />
                        </div>
                        <p className="text-xs text-white/90">
                          <strong>ISTQB</strong>
                        </p>
                      </div>
                      <div className="text-center">
                        <img 
                          src="https://images.credly.com/images/852e3e88-a0d3-4805-8ad9-6c2490ca2e20/image.png" 
                          alt="SAP BTP Developer" 
                          className="w-16 h-16 mb-1 mx-auto object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                        <div className="w-16 h-16 items-center justify-center bg-white/20 rounded-full mx-auto mb-1" style={{ display: 'none' }}>
                          <IconSolid name="certificate" className="w-8 h-8 text-white" />
                        </div>
                        <p className="text-xs text-white/90">
                          <strong>SAP BTP</strong>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column: Skills Grid */}
                <div className="grid grid-cols-1 gap-3">
                  <Card className="bg-white/15 border-white/30 text-white backdrop-blur-sm">
                    <div className="p-4">
                      <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                        <IconSolid name="brain" className="w-4 h-4" />
                        AI & Context Engineering
                      </h4>
                      <ul className="text-xs space-y-1 text-white/90 text-left">
                        <li>• Context Engineering • AI Engineering</li>
                        <li>• Prompt Engineering • RAG Architecture</li>
                        <li>• GitHub Copilot • Model Context Protocol</li>
                      </ul>
                    </div>
                  </Card>

                  <Card className="bg-white/15 border-white/30 text-white backdrop-blur-sm">
                    <div className="p-4">
                      <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                        <IconSolid name="laptop-code" className="w-4 h-4" />
                        Development
                      </h4>
                      <ul className="text-xs space-y-1 text-white/90 text-left">
                        <li>• TypeScript/JavaScript • Python • Java</li>
                        <li>• React • Vue.js • Flutter • Angular</li>
                        <li>• Spring Boot • FastAPI • SAP BTP</li>
                      </ul>
                    </div>
                  </Card>

                  <Card className="bg-white/15 border-white/30 text-white backdrop-blur-sm">
                    <div className="p-4">
                      <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                        <IconSolid name="sitemap" className="w-4 h-4" />
                        Requirements & Agile
                      </h4>
                      <ul className="text-xs space-y-1 text-white/90 text-left">
                        <li>• Requirements Engineering • Testing</li>
                        <li>• Scrum Master • UX/UI Design</li>
                        <li>• Process Optimization • Consulting</li>
                      </ul>
                    </div>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
      transition: 'fade',
    },

    // Slide 6: Our Approach
    {
      id: 6,
      component: () => (
        <EnhancedGridSlide
          title="🚀 Our Approach"
          subtitle="How we lead you to success"
          variant="light"
          columns={2}
          items={[
            <FeatureCard
              icon={<IconSolid name="rocket" className="w-12 h-12 text-blue-600" />}
              title="Enterprise-Ready"
              description="Scalable architectures with on-premise. Production-ready from the start."
              variant="default"
            />,
            <FeatureCard
              icon={<IconSolid name="brain" className="w-12 h-12 text-blue-600" />}
              title="AI Practice Since 2023"
              description="Daily work with Open-Source LLMs, RAG systems & AI integration."
              variant="default"
            />,
            <FeatureCard
              icon={<IconSolid name="shield-alt" className="w-12 h-12 text-blue-600" />}
              title="Privacy-First"
              description="On-premise or cloud - GDPR compliant and data secure."
              variant="default"
            />,
            <FeatureCard
              icon={<IconSolid name="users-cog" className="w-12 h-12 text-blue-600" />}
              title="Agile & Holistic"
              description="From requirements to development to deployment. Scrum, DevOps, CI/CD."
              variant="default"
            />,
          ]}
        />
      ),
      transition: 'slide',
    },

    // Slide 7: Client Context - Questions
    {
      id: 7,
      component: () => (
        <EnhancedGridSlide
          title="🔍 Understanding Your Situation"
          subtitle="Important questions for precise assessment"
          variant="light"
          columns={2}
          items={[
            <FeatureCard
              icon={<IconSolid name="tasks" className="w-12 h-12 text-blue-600" />}
              title="Processes & Tasks"
              description="Which tasks or processes would you like to optimize or automate?"
              items={['Identify use cases', 'Assess potential']}
              variant="default"
            />,
            <FeatureCard
              icon={<IconSolid name="brain" className="w-12 h-12 text-blue-600" />}
              title="AI Experience"
              description="Do you already have touchpoints with AI? (Copilot, ChatGPT, Open-Source LLMs)"
              items={['Determine maturity', 'Leverage experience']}
              variant="default"
            />,
            <FeatureCard
              icon={<IconSolid name="database" className="w-12 h-12 text-blue-600" />}
              title="Data Sources"
              description="What data is available? (Documents, databases, knowledge systems)"
              items={['Check quality', 'Secure access']}
              variant="default"
            />,
            <FeatureCard
              icon={<IconSolid name="shield-alt" className="w-12 h-12 text-blue-600" />}
              title="Compliance & Privacy"
              description="What requirements exist? Is on-prem or cloud solution possible?"
              items={['GDPR compliance', 'Security standards']}
              variant="default"
            />,
          ]}
        />
      ),
      transition: 'slide',
    },

    // Slide 8: AI Maturity Level
    {
      id: 8,
      component: () => (
        <EnhancedContentSlide
          title="📊 Determine AI Maturity"
          subtitle="Where are you currently?"
          variant="light"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-8">
            <Card className="flex-1 bg-slate-800 border-slate-600 text-center p-6 hover:shadow-lg transition-shadow">
              <IconSolid name="seedling" className="w-12 h-12 text-green-400 mx-auto mb-4" />
              <h4 className="text-xl font-semibold mb-2 text-white">Curious</h4>
              <p className="text-white/90 mb-2 font-medium">First Steps</p>
              <small className="text-white/70">"What can AI do?"</small>
            </Card>

            <div className="text-4xl text-blue-400 font-bold">→</div>

            <Card className="flex-1 bg-slate-800 border-slate-600 text-center p-6 hover:shadow-lg transition-shadow">
              <IconSolid name="flask" className="w-12 h-12 text-purple-400 mx-auto mb-4" />
              <h4 className="text-xl font-semibold mb-2 text-white">Experimental</h4>
              <p className="text-white/90 mb-2 font-medium">Trying Out</p>
              <small className="text-white/70">ChatGPT, Copilot</small>
            </Card>

            <div className="text-4xl text-blue-400 font-bold">→</div>

            <Card className="flex-1 bg-slate-800 border-slate-600 text-center p-6 hover:shadow-lg transition-shadow">
              <IconSolid name="project-diagram" className="w-12 h-12 text-blue-400 mx-auto mb-4" />
              <h4 className="text-xl font-semibold mb-2 text-white">Pilot Phase</h4>
              <p className="text-white/90 mb-2 font-medium">First Projects</p>
              <small className="text-white/70">Internal Testing</small>
            </Card>

            <div className="text-4xl text-blue-400 font-bold">→</div>

            <Card className="flex-1 bg-blue-900 border-blue-600 text-center p-6 shadow-lg">
              <IconSolid name="rocket" className="w-12 h-12 text-blue-300 mx-auto mb-4" />
              <h4 className="text-xl font-semibold mb-2 text-white">Production</h4>
              <p className="text-white/90 mb-2 font-medium">Live Operations</p>
              <small className="text-blue-300 font-semibold">Production-Ready</small>
            </Card>
          </div>

          <Card className="bg-blue-900 border-blue-600 mt-8">
            <div className="p-6 text-center">
              <p className="text-lg text-white font-medium">
                <strong>Important:</strong> Each phase has its own requirements.
                We meet you exactly where you are.
              </p>
            </div>
          </Card>
        </EnhancedContentSlide>
      ),
      transition: 'slide',
    },

    // Slide 9: Demo Announcement
    {
      id: 9,
      component: () => (
        <div className="relative w-full h-screen bg-gradient-to-br from-blue-900 to-slate-900 overflow-hidden">
          <div className="relative z-10 flex items-center justify-center h-full p-8 text-center">
            <div className="max-w-4xl space-y-12 text-white">
              <h1 className="text-6xl lg:text-7xl font-bold drop-shadow-lg">
                🚀 Live Demo
              </h1>
              <h2 className="text-3xl lg:text-4xl font-light drop-shadow-md">
                Our Enterprise RAG System in Action
              </h2>
              <p className="text-xl lg:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed">
                See how your team can retrieve internal knowledge data<br />
                faster and more precisely
              </p>
              <Card className="bg-white/20 border-white/30 backdrop-blur-md text-white mx-auto max-w-2xl">
                <div className="p-8 text-center">
                  <IconSolid name="check-circle" className="w-16 h-16 text-blue-300 mx-auto mb-6" />
                  <h4 className="text-2xl font-semibold mb-4">Standard LLM vs. RAG-LLM</h4>
                  <p className="text-white/90 text-lg leading-relaxed">
                    See the difference: General answers vs. contextualized,
                    fact-based answers with source citations
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </div>
      ),
      transition: 'fade',
    },

    // Slide 10: Service Modules
    {
      id: 10,
      component: () => (
        <EnhancedGridSlide
          title="🧩 Modular Service Offerings"
          subtitle="Flexible solutions for your requirements"
          variant="light"
          columns={2}
          items={[
            <FeatureCard
              icon={<IconSolid name="graduation-cap" className="w-12 h-12 text-blue-600" />}
              title="AI Strategy & Training"
              description="Enable employees, find your own use cases"
              items={[
                'Developer workshops',
                'Prompt engineering training',
                'Best practice sessions',
              ]}
              variant="default"
            />,
            <FeatureCard
              icon={<IconSolid name="server" className="w-12 h-12 text-blue-600" />}
              title="RAG System & Knowledge Platform"
              description="Make your own data usable securely with LLMs"
              items={[
                'On-prem or cloud (Open-Source LLMs)',
                'Qdrant vector database',
                'FastAPI backend',
              ]}
              variant="default"
            />,
            <FeatureCard
              icon={<IconSolid name="puzzle-piece" className="w-12 h-12 text-blue-600" />}
              title="AI Integration into Systems"
              description="Efficiency gains through automation"
              items={[
                'Chatbot integration',
                'Agent workflows',
                'API development',
              ]}
              variant="default"
            />,
            <FeatureCard
              icon={<IconSolid name="code" className="w-12 h-12 text-blue-600" />}
              title="Development & Support"
              description="Tailored solutions"
              items={[
                'Rapid prototyping',
                'Custom development',
                'Deployment & operations',
              ]}
              variant="default"
            />,
          ]}
        />
      ),
      transition: 'slide',
    },

    // Slide 11: Best Practices Insights
    {
      id: 11,
      component: () => (
        <div className="relative w-full h-screen bg-gradient-to-br from-teal-600 to-teal-900 overflow-hidden">
          <div className="relative z-10 p-8 lg:p-16 h-full flex flex-col justify-center pb-24">
            <h2 className="text-5xl lg:text-6xl font-bold text-white mb-4 text-center">💎 Quick Wins</h2>
            <h3 className="text-2xl lg:text-3xl text-white/90 mb-12 text-center">Immediately Actionable Best Practices</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-7xl mx-auto">
              <Card className="bg-slate-800/95 border-slate-600 backdrop-blur-sm">
                <div className="p-8">
                  <IconSolid name="brain" className="w-16 h-16 text-blue-400 mx-auto mb-6" />
                  <h4 className="text-2xl font-semibold mb-4 text-white text-center">Context Engineering</h4>
                  <p className="text-white/90 mb-6 text-center font-medium">
                    The new prompt engineering - up to 70% better results through structured contexts
                  </p>
                  <div className="bg-slate-900/80 p-6 rounded-lg text-sm space-y-4">
                    <div>
                      <strong className="text-white text-base">Context &gt; Prompts:</strong>
                      <div className="mt-2 space-y-2">
                        <div className="text-white/90">❌ <span className="text-red-400 font-semibold">"Write a text"</span></div>
                        <div className="pl-4 border-l-4 border-green-500 space-y-1">
                          <div className="text-white/90">✅ <strong className="text-green-400">Context:</strong> "Business context, target audience CTOs, focus on business value"</div>
                          <div className="text-white/90">✅ <strong className="text-green-400">Role:</strong> "Technical editor with 10 years experience"</div>
                          <div className="text-white/90">✅ <strong className="text-green-400">Task:</strong> "200-word text about RAG benefits"</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="bg-slate-800/95 border-slate-600 backdrop-blur-sm">
                <div className="p-8">
                  <IconSolid name="chart-line" className="w-16 h-16 text-blue-400 mx-auto mb-6" />
                  <h4 className="text-2xl font-semibold mb-4 text-white text-center">RAG vs. Standard LLM</h4>
                  <p className="text-white/90 mb-6 text-center font-medium">
                    Why RAG for enterprise data?
                  </p>
                  <div className="bg-slate-900/80 p-6 rounded-lg text-sm space-y-4">
                    <div>
                      <strong className="text-white text-base">Standard LLM:</strong>
                      <ul className="mt-2 space-y-1 text-red-400 font-medium">
                        <li>• Outdated knowledge</li>
                        <li>• Hallucinations</li>
                        <li>• No sources</li>
                      </ul>
                    </div>
                    <div>
                      <strong className="text-green-400 text-base">RAG System:</strong>
                      <ul className="mt-2 space-y-1 text-green-400 font-medium">
                        <li>• Current data</li>
                        <li>• Fact-based</li>
                        <li>• Source citations</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      ),
      transition: 'fade',
    },

    // Slide 12: Typical Use Cases
    {
      id: 12,
      component: () => (
        <EnhancedGridSlide
          title="💼 Typical Use Cases"
          subtitle="Where AI brings real value"
          variant="light"
          columns={2}
          items={[
            <FeatureCard
              icon={<IconSolid name="book-open" className="w-12 h-12 text-blue-600" />}
              title="Knowledge Management"
              description="Make internal documentation searchable. Accelerate onboarding."
              variant="default"
            />,
            <FeatureCard
              icon={<IconSolid name="headset" className="w-12 h-12 text-teal-600" />}
              title="Customer Support"
              description="24/7 chatbot with access to support database and FAQs."
              variant="default"
            />,
            <FeatureCard
              icon={<IconSolid name="file-alt" className="w-12 h-12 text-amber-600" />}
              title="Contract Analysis"
              description="Automatic extraction of relevant clauses from contracts."
              variant="default"
            />,
            <FeatureCard
              icon={<IconSolid name="robot" className="w-12 h-12 text-green-600" />}
              title="Code Assistance"
              description="Developer support with internal codebase and best practices."
              variant="default"
            />,
          ]}
        />
      ),
      transition: 'slide',
    },

    // Slide 13: Next Steps
    {
      id: 13,
      component: () => (
        <div className="relative w-full h-screen bg-gradient-to-br from-blue-900 to-slate-900 overflow-hidden">
          <div className="relative z-10 p-6 lg:p-8 h-full flex flex-col justify-center pb-28">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-2 text-center">🎯 Next Steps</h2>
            <h3 className="text-lg lg:text-xl text-white/90 mb-4 text-center">What comes next?</h3>

            <div className="flex flex-col gap-1.5 max-w-4xl mx-auto">
              <Card className="bg-slate-800/95 border-slate-600">
                <div className="p-2.5 lg:p-3">
                  <h4 className="text-sm font-semibold mb-0.5 flex items-center gap-2 text-white">
                    <IconSolid name="clipboard-check" className="w-4 h-4 text-blue-400" />
                    Today
                  </h4>
                  <p className="text-white/90 text-xs">
                    We summarize: Which areas have the greatest potential?
                  </p>
                </div>
              </Card>

              <div className="text-center text-lg text-white font-bold">↓</div>

              <Card className="bg-slate-800/95 border-slate-600">
                <div className="p-2.5 lg:p-3">
                  <h4 className="text-sm font-semibold mb-0.5 flex items-center gap-2 text-white">
                    <IconSolid name="lightbulb" className="w-4 h-4 text-blue-400" />
                    Option 1: Workshop
                  </h4>
                  <p className="text-white/90 text-xs">
                    Analysis session (2-4h) • Identify use cases • Develop roadmap
                  </p>
                </div>
              </Card>

              <div className="text-center text-lg text-white font-bold">↓</div>

              <Card className="bg-slate-800/95 border-slate-600">
                <div className="p-2.5 lg:p-3">
                  <h4 className="text-sm font-semibold mb-0.5 flex items-center gap-2 text-white">
                    <IconSolid name="rocket" className="w-4 h-4 text-blue-400" />
                    Option 2: Pilot Project
                  </h4>
                  <p className="text-white/90 text-xs">
                    RAG prototype with your data • Prove feasibility • Quick win
                  </p>
                </div>
              </Card>

              <div className="text-center text-lg text-white font-bold">↓</div>

              <Card className="bg-slate-800/95 border-slate-600">
                <div className="p-2.5 lg:p-3">
                  <h4 className="text-sm font-semibold mb-0.5 flex items-center gap-2 text-white">
                    <IconSolid name="graduation-cap" className="w-4 h-4 text-blue-400" />
                    Option 3: Training
                  </h4>
                  <p className="text-white/90 text-xs">
                    Developer workshop (1-3 days) • Hands-on training • Start your own projects
                  </p>
                </div>
              </Card>

              <div className="text-center text-lg text-white font-bold">↓</div>

              <Card className="bg-slate-800/95 border-slate-600">
                <div className="p-2.5 lg:p-3">
                  <h4 className="text-sm font-semibold mb-0.5 flex items-center gap-2 text-white">
                    <IconSolid name="video" className="w-4 h-4 text-blue-400" />
                    Option 4: Video Course
                  </h4>
                  <p className="text-white/90 text-xs">
                    Flexible remote learning • Self-paced • Practical content & examples
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </div>
      ),
      transition: 'fade',
    },

    // Slide 14: Contact
    {
      id: 14,
      component: () => (
        <div className="relative w-full h-screen bg-gradient-to-br from-slate-600 to-slate-900 overflow-hidden">
          <div className="relative z-10 flex items-center justify-center h-full p-8 text-center pb-24">
            <div className="max-w-5xl space-y-8 text-white">
              <h2 className="text-5xl lg:text-6xl font-bold">Thank You!</h2>
              <h3 className="text-3xl lg:text-4xl font-light">Your Questions & Our Contact</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
                <Card className="bg-slate-800/95 border-slate-600 text-white">
                  <div className="p-8 text-center space-y-4">
                    <img 
                      src="/images/jan.png" 
                      alt="Jan Pfrommer"
                      className="w-24 h-24 rounded-2xl object-cover mx-auto"
                    />
                    <h4 className="text-2xl font-semibold">Jan Pfrommer</h4>
                    <p className="flex items-center justify-center gap-2 text-white/90">
                      <IconSolid name="envelope" className="w-5 h-5 text-blue-400" />
                      jan.pfrommer@securesight.ai
                    </p>
                    <p className="flex items-center justify-center gap-2 text-white/90">
                      <img 
                        src="https://content.linkedin.com/content/dam/me/business/en-us/amp/xbu/linkedin-revised-brand-guidelines/linkedin-logo/fg/brandg-linkedinlogo-logo-background-dsk-v03.png.original.png" 
                        alt="LinkedIn" 
                        className="w-5 h-5 object-contain"
                      />
                      linkedin.com/in/janpfrommer
                    </p>
                  </div>
                </Card>

                <Card className="bg-slate-800/95 border-slate-600 text-white">
                  <div className="p-8 text-center space-y-4">
                    <img 
                      src="/images/kai.png" 
                      alt="Kai Bennett"
                      className="w-24 h-24 rounded-2xl object-cover mx-auto"
                    />
                    <h4 className="text-2xl font-semibold">Kai Bennett</h4>
                    <p className="flex items-center justify-center gap-2 text-white/90">
                      <IconSolid name="envelope" className="w-5 h-5 text-blue-400" />
                      kai.bennett@securesight.ai
                    </p>
                    <p className="flex items-center justify-center gap-2 text-white/90">
                      <img 
                        src="https://content.linkedin.com/content/dam/me/business/en-us/amp/xbu/linkedin-revised-brand-guidelines/linkedin-logo/fg/brandg-linkedinlogo-logo-background-dsk-v03.png.original.png" 
                        alt="LinkedIn" 
                        className="w-5 h-5 object-contain"
                      />
                      linkedin.com/in/kai
                    </p>
                  </div>
                </Card>
              </div>

              <div className="flex flex-col sm:flex-row gap-6 justify-center mt-8">
                <button className="px-8 py-4 bg-white text-blue-600 rounded-xl font-semibold text-lg flex items-center justify-center gap-3 hover:bg-blue-50 transition-colors shadow-lg">
                  <IconSolid name="calendar-alt" className="w-6 h-6" />
                  Schedule Follow-up
                </button>
                <button className="px-8 py-4 bg-transparent text-white border-2 border-white rounded-xl font-semibold text-lg flex items-center justify-center gap-3 hover:bg-white/10 transition-colors">
                  <IconSolid name="file-alt" className="w-6 h-6" />
                  Presentation as PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      ),
      transition: 'fade',
    },
  ],
}

export default erstgespraechEn
